import { DetailTable } from "../../components/DetailTable";
import { JumpToSection } from "../../components/JumpToSection";
import { useFinanceLab } from "@/entities/FinanceLab";
import { InfoTooltip } from "@/ui/molecules/InfoTooltip";
import { useAtomValue } from "jotai";
import { formatAsOfDate } from "../../helpers/formatAsOfDate";
import { BALANCE_SHEET_CODES, INCOME_STATEMENT_CODES } from "./codes";
import {
    DeltaType,
    MetricConfig,
    PeriodResolution,
} from "@/features/Metrics/Metrics.types";
import { asOfDateAtom } from "@/features/Metrics/state/Metrics.state";
import { useCapitalStructureData } from "./useCapitalStructureData";
import { DetailGrid } from "../../components/DetailGrid/DetailGrid";
import { METRIC_CONFIG } from "@/features/Metrics/METRIC_CONFIG";
import {
    DetailGridMetric,
    DetailGridMetricQuery,
} from "../../components/DetailGrid/DetailGridMetric";
import { MetricQueryString } from "@/entities/Finance/models/MetricQueryString.model";

export function FinancialHighlights() {
    const { isPolling } = useFinanceLab();
    const asOfDate = useAtomValue(asOfDateAtom);
    const capitalStructureData = useCapitalStructureData();

    const isLoadingMetrics = isPolling;

    const formattedAsOfDate = formatAsOfDate(asOfDate.format("MM/DD/YYYY"));
    return (
        <>
            <div className="space-y-6">
                <div className="flex flex-col gap-2">
                    <div className="body-s flex items-center gap-1">
                        <span className="subdued italic">
                            Data as of {formattedAsOfDate}
                        </span>
                        <InfoTooltip>
                            The data displayed is from the last closed, complete
                            calendar period available in the company&apos;s
                            accounting system. Hum does not display partial
                            period data.
                        </InfoTooltip>
                    </div>
                    <p className="body-s">
                        Income Statement values are presented annually. Balance
                        Sheet values are presented monthly. Total Equity Raised
                        is not affected by timeframe. Click any metric to see an
                        interactive deep dive in the Financials and Projections
                        section.
                    </p>
                </div>
                <DetailGrid title="Income Statement" eyebrow="LTM">
                    {INCOME_STATEMENT_CODES.map((code) => (
                        <DetailGridMetricQuery
                            key={code}
                            config={METRIC_CONFIG[code] as MetricConfig}
                            panelTitle={`${METRIC_CONFIG[code].label} - Last 12 Months`}
                            query={new MetricQueryString(code)
                                .monthly()
                                .window(12)
                                .completeSum()
                                .applyBeforeIf(
                                    METRIC_CONFIG[code].type === "percentage",
                                )}
                            deltaType={DeltaType.CUMULATIVE}
                        />
                    ))}
                </DetailGrid>

                <DetailGrid title="Balance Sheet" eyebrow="Last Closed Month">
                    {BALANCE_SHEET_CODES.map((code) => (
                        <DetailGridMetric
                            key={code}
                            config={METRIC_CONFIG[code] as MetricConfig}
                            resolution={PeriodResolution.MONTHLY}
                            deltaType={DeltaType.PERIOD_OVER_PERIOD}
                            panelTitle={`${METRIC_CONFIG[code].label} - Last Closed Month`}
                        />
                    ))}
                </DetailGrid>
                <DetailTable
                    title="Capital Structure"
                    data={capitalStructureData}
                    isLoading={isLoadingMetrics}
                />
            </div>
            <JumpToSection
                targetSection="financial-highlights"
                label="Jump to Financials & Projections"
            />
        </>
    );
}
